import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/core/api/client";
import type { PublicConfig } from "./types";

const TenantContext = createContext<PublicConfig | null>(null);

function hexToRgbTriplet(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

export function applyBranding(config: PublicConfig): void {
  const root = document.documentElement;
  const primary = hexToRgbTriplet(config.branding.primary_color);
  const accent = hexToRgbTriplet(config.branding.accent_color);
  const surface = hexToRgbTriplet(config.branding.background_color);
  if (primary) root.style.setProperty("--color-primary", primary);
  if (accent) root.style.setProperty("--color-accent", accent);
  if (surface) root.style.setProperty("--color-surface", surface);
  document.title = config.branding.display_name || config.name;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["tenant", "public-config"],
    queryFn: () => api<PublicConfig>("/api/tenant/public-config/"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (query.data) {
      applyBranding(query.data);
      if (!localStorage.getItem("i18nextLng")) void i18n.changeLanguage(query.data.locale_default);
    }
  }, [query.data, i18n]);

  if (query.isPending) {
    return <div className="bt-page-message">{t("app.loading")}</div>;
  }
  if (query.isError || !query.data) {
    return <div className="bt-page-message bt-page-message--error">{t("tenant.unknown")}</div>;
  }
  return <TenantContext.Provider value={query.data}>{children}</TenantContext.Provider>;
}

export function useTenant(): PublicConfig {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}
